import {useEffect, useState} from 'react'
import {useQuery} from '@tanstack/react-query'

import {createQueryKey} from '#/state/queries/util'
import {createQrSession, pollQrSession} from './api'

const qrLoginSessionQueryKeyRoot = 'qrLoginSession'

export const createQrLoginSessionQueryKey = (args: {
  serviceUrl: string
  nonce: number
}) => createQueryKey(qrLoginSessionQueryKeyRoot, args)

const qrLoginPollQueryKeyRoot = 'qrLoginPoll'

export const createQrLoginPollQueryKey = (args: {
  serviceUrl: string
  verkey: string
}) => createQueryKey(qrLoginPollQueryKeyRoot, args)

/**
 * Fetches a QR login session from the PDS. `refresh` discards the current
 * session and requests a fresh one (new keypair, new QR code).
 */
export function useQrLoginSessionQuery({
  serviceUrl,
  enabled = true,
}: {
  serviceUrl: string
  enabled?: boolean
}) {
  const [nonce, setNonce] = useState(0)

  const query = useQuery({
    queryKey: createQrLoginSessionQueryKey({serviceUrl, nonce}),
    queryFn: () => createQrSession(serviceUrl),
    enabled,
    gcTime: 0,
    staleTime: Infinity,
    retry: 1,
  })

  /*
   * The session keypair expires server-side after `expiresIn` seconds. Track
   * expiry locally so we can stop polling and offer a refresh. Keyed by
   * verkey so a refreshed session is never born expired.
   */
  const [expiredVerkey, setExpiredVerkey] = useState<string | null>(null)
  const sessionVerkey = query.data?.verkey
  const expiresIn = query.data?.expiresIn
  useEffect(() => {
    if (!sessionVerkey || !expiresIn) return
    const timer = setTimeout(
      () => setExpiredVerkey(sessionVerkey),
      expiresIn * 1000,
    )
    return () => clearTimeout(timer)
  }, [sessionVerkey, expiresIn])

  return {
    ...query,
    expired: !!sessionVerkey && expiredVerkey === sessionVerkey,
    refresh: () => setNonce(n => n + 1),
  }
}

/**
 * Polls the PDS every 2 seconds until the phone approves the session, at
 * which point the query data flips to `{status: 'ready', tokens}`.
 */
export function useQrLoginPollQuery({
  serviceUrl,
  verkey,
  enabled,
}: {
  serviceUrl: string
  verkey: string | undefined
  enabled: boolean
}) {
  return useQuery({
    queryKey: createQrLoginPollQueryKey({serviceUrl, verkey: verkey ?? ''}),
    queryFn: () => pollQrSession(serviceUrl, verkey!),
    enabled: enabled && !!verkey,
    gcTime: 0,
    refetchInterval: query =>
      query.state.data?.status === 'ready' ? false : 2000,
    refetchIntervalInBackground: false,
  })
}
